import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  CalendarPlus, CheckCircle2, XCircle, Users, Clock, StickyNote, Ban, CalendarClock,
} from 'lucide-react';
import { PageHeader, Button, Avatar } from '../../components';
import { useAuth } from '../../context/AuthContext';
import { useLiveRefresh } from '../../context/LiveUpdatesContext';
import openShiftsService from '../../services/carehome_manager/openShiftsService';
import { FilterTabs, EmptyCard, ReasonModal, CARD } from '../agency/shared';
import OfferShiftModal from './available_shifts/OfferShiftModal';
import { SHIFT_STATE, REQUEST_STATE, StatePill, errorMessage } from './available_shifts/shared';

/**
 * Rota > Available Shifts — care homes only.
 *
 * The home offers an extra shift ("Offer shift" here or on a team in the
 * rota). Carers free for it are told and request it in the app; here the
 * manager sees each request with the checks that make the carer free for it
 * (day off, no overlapping shift, not on leave, role) — re-checked live — and
 * approves one, which puts a scheduled shift on that carer's rota, or
 * declines. When every place is filled the others are told it has gone.
 *
 *   Open     still to happen (open or filled)
 *   History  started, or cancelled
 */

const ShiftFacts = ({ shift }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] text-[#64748B] dark:text-gray-400">
    <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />{shift.filled} of {shift.places} place{shift.places === 1 ? '' : 's'} filled</span>
    {shift.job_role && <span>Role: <b className="text-[#334155] dark:text-gray-200">{shift.job_role}</b></span>}
    {shift.state === 'open' && <span>{shift.notified_count} carer{shift.notified_count === 1 ? '' : 's'} were free when published</span>}
  </div>
);

const Checks = ({ checks }) => (
  <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
    {checks.map((c) => (
      <li key={c.key} className={`flex items-start gap-1.5 text-[12px] ${c.ok ? 'text-[#047857] dark:text-emerald-400' : 'text-[#B91C1C] dark:text-rose-400'}`}>
        {c.ok ? <CheckCircle2 className="w-3.5 h-3.5 mt-[1px] shrink-0" /> : <XCircle className="w-3.5 h-3.5 mt-[1px] shrink-0" />}
        <span>{c.label}</span>
      </li>
    ))}
  </ul>
);

const RequestRow = ({ request, shiftOpen, busy, onApprove, onDecline }) => {
  const waiting = request.status === 'requested';
  return (
    <div className="px-4 py-3 border-t border-[#EEF2F7] dark:border-gray-800">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar name={request.user.name} image={request.user.avatar_url} size={36} />
          <div className="min-w-0">
            <div className="text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100 truncate">{request.user.name}</div>
            <div className="text-[12px] text-[#94A3B8] dark:text-gray-500 truncate">
              {[request.user.job_title, request.user.user_id].filter(Boolean).join(' · ')}
              {request.requested_at && ` · asked ${new Date(request.requested_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {waiting && shiftOpen ? (
            <>
              <Button size="sm" variant="secondary" onClick={onDecline} disabled={busy}>Decline</Button>
              <Button
                size="sm"
                variant="primary"
                onClick={onApprove}
                loading={busy}
                disabled={request.eligible === false}
                title={request.eligible === false ? 'This carer is no longer free for this shift' : undefined}
              >
                Approve &amp; assign
              </Button>
            </>
          ) : (
            <StatePill map={REQUEST_STATE} state={request.status} />
          )}
        </div>
      </div>
      {waiting && shiftOpen && request.checks?.length > 0 && <Checks checks={request.checks} />}
      {request.reason && !waiting && (
        <p className="mt-1.5 text-[12px] text-[#64748B] dark:text-gray-400">{request.reason}</p>
      )}
    </div>
  );
};

const ShiftCard = ({ shift, busyId, onApprove, onDecline, onCancel }) => {
  const waiting = shift.requests.filter((r) => r.status === 'requested');
  const others = shift.requests.filter((r) => r.status !== 'requested');
  const shiftOpen = shift.state === 'open';
  return (
    <section className={`${CARD} overflow-hidden`}>
      <div className="px-4 py-3.5 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[15px] font-bold text-[#0B1220] dark:text-gray-100">{shift.when}</span>
            <StatePill map={SHIFT_STATE} state={shift.state} />
            {shift.group && (
              <span className="px-2 py-0.5 rounded-md bg-[#F2F6FC] dark:bg-[#31373f] text-[#53657d] dark:text-gray-400 text-[11px] font-extrabold uppercase tracking-wide">{shift.group.name}</span>
            )}
          </div>
          <ShiftFacts shift={shift} />
          {shift.note && (
            <p className="flex items-start gap-1.5 text-[12.5px] text-[#475569] dark:text-gray-300"><StickyNote className="w-3.5 h-3.5 mt-[2px] shrink-0" />{shift.note}</p>
          )}
          {shift.state === 'cancelled' && shift.cancel_reason && (
            <p className="text-[12.5px] text-[#64748B] dark:text-gray-400">Cancelled: {shift.cancel_reason}</p>
          )}
        </div>
        {(shift.state === 'open' || shift.state === 'filled') && (
          <Button size="sm" variant="ghost" leftIcon={<Ban className="w-3.5 h-3.5" />} onClick={() => onCancel(shift)}>
            Cancel offer
          </Button>
        )}
      </div>

      {shift.requests.length === 0 ? (
        <div className="px-4 py-3 border-t border-[#EEF2F7] dark:border-gray-800 text-[12.5px] text-[#94A3B8] dark:text-gray-500 flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" />{shiftOpen ? 'No requests yet — carers who are free can ask for it in the app.' : 'Nobody requested this shift.'}
        </div>
      ) : (
        <>
          {waiting.length > 0 && shiftOpen && (
            <div className="px-4 pt-2.5 pb-1 border-t border-[#EEF2F7] dark:border-gray-800 bg-amber-50/50 dark:bg-amber-500/[0.05] text-[11px] font-extrabold uppercase tracking-wider text-amber-700 dark:text-amber-300">
              {waiting.length} request{waiting.length === 1 ? '' : 's'} to review
            </div>
          )}
          {[...waiting, ...others].map((r) => (
            <RequestRow
              key={r.id}
              request={r}
              shiftOpen={shiftOpen}
              busy={busyId === r.id}
              onApprove={() => onApprove(r)}
              onDecline={() => onDecline(r)}
            />
          ))}
        </>
      )}
    </section>
  );
};

const AvailableShifts = () => {
  const { user } = useAuth();
  const isDomiciliary = (user?.organization?.organization_type || '').toLowerCase() === 'domiciliary';
  const [view, setView] = useState('open');
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ pending_requests: 0 });
  const [loading, setLoading] = useState(true);
  const [offerOpen, setOfferOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [declining, setDeclining] = useState(null); // request
  const [cancelling, setCancelling] = useState(null); // shift

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const res = await openShiftsService.list(view);
      setRows(res?.data || []);
      setMeta(res?.meta || {});
    } catch (error) {
      if (!quiet) toast.error(errorMessage(error, 'Could not load available shifts.'));
    } finally {
      setLoading(false);
    }
  }, [view]);
  useEffect(() => { if (!isDomiciliary) load(); }, [load, isDomiciliary]);
  // A carer's request, another manager's decision or a rota change elsewhere.
  useLiveRefresh(['rota'], () => { if (!isDomiciliary) load({ quiet: true }); });

  const approve = async (request) => {
    setBusyId(request.id);
    try {
      const res = await openShiftsService.approve(request.id);
      toast.success(res?.message || 'Approved.');
    } catch (error) {
      toast.error(errorMessage(error, 'Could not approve the request.'));
    } finally {
      setBusyId(null);
      load({ quiet: true });
    }
  };

  if (isDomiciliary) {
    return <EmptyCard title="Available shifts are for care homes" text="Offering extra shifts to carers is available to care-home organisations." />;
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Available Shifts"
        subtitle="Offer extra shifts to carers who are free. They request them in the app; you choose who gets each one."
        actions={(
          <Button leftIcon={<CalendarPlus className="w-4 h-4" />} onClick={() => setOfferOpen(true)}>Offer shift</Button>
        )}
      />

      <FilterTabs
        value={view}
        onChange={setView}
        tabs={[
          { value: 'open', label: 'Open', count: view === 'open' && meta.pending_requests ? meta.pending_requests : undefined, alert: true },
          { value: 'history', label: 'History' },
        ]}
      />

      {loading ? (
        <div className={`${CARD} px-6 py-14 text-center text-[13px] text-[#94A3B8]`}>Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyCard
          icon={CalendarClock}
          title={view === 'open' ? 'No shifts on offer' : 'Nothing here yet'}
          text={view === 'open'
            ? 'Offer a shift and every carer who is free for it is told in the app.'
            : 'Shifts that have started or were cancelled appear here for 60 days.'}
          action={view === 'open' ? <Button leftIcon={<CalendarPlus className="w-4 h-4" />} onClick={() => setOfferOpen(true)}>Offer shift</Button> : null}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {rows.map((shift) => (
            <ShiftCard
              key={shift.id}
              shift={shift}
              busyId={busyId}
              onApprove={approve}
              onDecline={setDeclining}
              onCancel={setCancelling}
            />
          ))}
        </div>
      )}

      <OfferShiftModal isOpen={offerOpen} onClose={() => setOfferOpen(false)} onCreated={() => load({ quiet: true })} />

      <ReasonModal
        open={!!declining}
        title="Decline request"
        intro={declining ? `${declining.user.name} will be told their request was declined.` : ''}
        label="Reason (optional)"
        required={false}
        placeholder="e.g. we need a senior on this shift"
        confirmLabel="Decline"
        onClose={() => setDeclining(null)}
        onSubmit={async (reason) => {
          const res = await openShiftsService.decline(declining.id, reason);
          toast.success(res?.message || 'Declined.');
          setDeclining(null);
          load({ quiet: true });
        }}
      />

      <ReasonModal
        open={!!cancelling}
        title="Cancel this offer"
        intro={cancelling ? `${cancelling.when}. Carers still waiting are told; anyone already approved keeps the shift on their rota — remove it there if it is no longer needed.` : ''}
        label="Reason (optional)"
        required={false}
        confirmLabel="Cancel offer"
        onClose={() => setCancelling(null)}
        onSubmit={async (reason) => {
          const res = await openShiftsService.cancel(cancelling.id, reason);
          toast.success(res?.message || 'Cancelled.');
          setCancelling(null);
          load({ quiet: true });
        }}
      />
    </div>
  );
};

export default AvailableShifts;
